// src/components/assistant/ChatPane.tsx
//
// El panel de CONVERSACIÓN (mensajes + composer). Controlado por `threadId`:
//  - threadId=null  → inicia/recupera el hilo del usuario (POST /threads/start)
//    y avisa al padre con onThreadStarted (para refrescar el historial).
//  - threadId=<id>  → carga los mensajes de ESE hilo (reabrir desde el historial).
//
// Contrato backend (mismo que WebChat, verificado):
//   POST {base}/threads/start                 -> { thread_id }
//   GET  {base}/threads/:id/messages          -> { messages: [{ id, role, bloques, at }] }
//   POST {base}/threads/:id/messages          -> { message:{role,bloques} } | { bloques } | ACK
//   POST {base}/threads/:id/messages/stream   -> SSE (status/delta/reset/bloque/done/error), ver
//        ./chatStream.ts. Se usa primero; si no existe o un proxy lo rompe antes del
//        primer evento, se cae al POST JSON de arriba sin que el usuario lo note.
// Auth: Authorization: Bearer <auth_token>  +  ...webChatHeaders (incluye x-org-id).

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Send, Bot, User, Brain, ChevronDown, Sparkles, Square, Paperclip, Mic, Loader2, X, Maximize2, Minimize2, FileText, Table2, Wrench } from 'lucide-react';
import { apiUrl } from '@/config';
import { getChatModels, getMyThreads, renameThread, type ChatModelChoice, type ChatModelsInfo } from '@/lib/api';
import { ATTACH_ACCEPT, AttachmentCard, splitAttachments, uploadChatAttachment, type PendingAttachment } from './attachments';
import { setActiveThread as shareActiveThread } from './activeThread';
import { ThreadHeaderTitle } from './ThreadTitle';
import { listAssistants } from '@/modules/assistants/api';
import type { AssistantOut } from '@/modules/assistants/types';
import { ModelPicker, sovereignOnly } from './ModelPicker';
import { orgIdFromToken } from '@/lib/api_superadmin';
import { streamThreadMessage, StreamHttpError, type StreamEvent } from './chatStream';
import { capturarPantalla } from '@/lib/pantalla';
import { MentionChip, MentionMenu, optionId } from './MentionMenu';
import { activeTrigger, filterItems, loadSkills, loadTools, MAX_TOOLS, type MentionItem, type MentionKind } from './mentions';
import { VisualBloque } from '@/components/visuals/VisualBloque';
import { PropuestaDiagnosticoBloque } from '@/modules/diagnostics/components/PropuestaDiagnosticoCard';
import { MediaSave } from './MediaSave';
import { MessageContent, isWideMarkdown, preloadRichText } from './MessageContent';
import { MessageActions } from './MessageActions';

interface Bloque {
  tipo: string; // 'texto' | 'imagen' | 'video' | 'audio' | 'pdf' | 'visual' | 'propuesta_diagnostico'
  contenido: string;
  /** Solo en tipo 'visual': contrato Orion Visual v1 (@orion/visuals). */
  visual?: unknown;
  /** Solo en tipo 'propuesta_diagnostico': la propuesta del Configurador (CONFIGURADOR.md). */
  propuesta?: unknown;
}

// Bloques con texto legible en `contenido` (para copiar y anunciar).
const TEXTUALES = new Set(['texto', 'visual', 'propuesta_diagnostico']);

// Turnos en curso por hilo, a nivel módulo: sobreviven al desmontaje del ChatPane
// (minimizar al orbe, ampliar, cambiar de pantalla). Quien abra ese hilo espera a que
// termine y recarga los mensajes. Se libera SIEMPRE al cerrar el stream (también al
// «Detener» o con error), así que nunca deja un hilo bloqueado.
const turnosEnCurso = new Map<string, Promise<void>>();

// Respuesta que necesita todo el ancho: visual, código o tabla (se desplazan DENTRO).
const isWide = (bloques: Bloque[]) =>
  bloques.some((b) => b.tipo === 'visual' || b.tipo === 'propuesta_diagnostico' || (b.tipo === 'texto' && isWideMarkdown(b.contenido)));

interface ChatMsg {
  id: string;
  role: 'user' | 'assistant';
  bloques: Bloque[];
  /** 🧠 Razonamiento profundo del modelo soberano (solo con "Pensar" ON). */
  thinking?: string | null;
  /** La persona detuvo la respuesta: lo que alcanzó a escribirse (no se guardó). */
  stopped?: boolean;
  /** Menciones con las que viajó el mensaje del usuario (agente, skill, tools). */
  mentions?: MentionItem[];
  /** Agente que respondió (si el mensaje iba con @agente). */
  agent?: MentionItem | null;
  /** 🔧 Pasos del agente durante el turno (eventos `status` del stream). */
  steps?: string[];
}

interface ChatPaneProps {
  /** Hilo a mostrar. null = iniciar/recuperar uno nuevo. */
  threadId?: string | null;
  /** Si true y threadId es null, fuerza la CREACIÓN de un thread nuevo
   *  (new_thread:true) en vez de recuperar el determinístico del usuario.
   *  Lo usa el botón "Nuevo chat". */
  startFresh?: boolean;
  /** Headers extra del shell (incluye x-org-id). */
  webChatHeaders?: Record<string, string>;
  /** Avisa cuando se resuelve/crea el thread_id (para refrescar historial). */
  onThreadStarted?: (threadId: string) => void;
  /** Oculta el toggle "Pensar". El diagnóstico no usa razonamiento profundo:
   *  es una entrevista conversacional, no una tarea de análisis. */
  hideThinking?: boolean;
  /** Iniciadores de conversación de ESTA pantalla. Sin esto se muestran los
   *  genéricos del asistente, que en un contexto acotado confunden. */
  suggestions?: string[];
  /** Enfoque de tools: el módulo de esta pantalla (ej. "org-scan"). El backend
   *  acota el catálogo del agente a ese módulo + discovery. undefined = todo. */
  focusTools?: string;
  /** Avatar del asistente (URL). Opcional: sin él se usa el ícono de siempre. */
  assistantAvatarUrl?: string;
  /** Se llama con el texto que envía el usuario (p. ej. navegación por intención del dock). */
  onUserSend?: (text: string) => void;
  /** Agente activo controlado desde fuera (opcional). Sin esto, ChatPane
   *  maneja su propio selector como siempre. */
  assistantId?: string;
  /** Oculta el selector interno de agente (cuando el padre pinta el suyo). */
  hideAgentSelector?: boolean;
  /** Sugerencias con ícono (opcional; tiene prioridad sobre `suggestions`). */
  suggestionItems?: Array<{ text: string; icon?: React.ReactNode }>;
  /** Título y subtítulo del estado vacío. */
  emptyTitle?: string;
  emptySubtitle?: string;
  /** Muestra el selector de modelo/esfuerzo junto al input (opt-in por pantalla).
   *  Si el backend no tiene la API o la org no ofrece modelos, no aparece. */
  modelPicker?: boolean;
  /** Avisa el título del hilo al cargarlo (para el encabezado). */
  onThreadInfo?: (threadId: string, info: { title: string | null; titleSource: string | null }) => void;
  /** El backend está nombrando el hilo en segundo plano (tras el 1er intercambio). */
  onTitlePending?: (threadId: string) => void;
  /** Menciones del compositor (@ agente, / skill, # herramienta). Opt-in: solo
   *  pantallas de staff (/chat, Asistente del shell, panel del Inicio). El
   *  diagnóstico (empleados de clientes) NO las activa. */
  mentions?: boolean;
  /** Contexto de la pantalla que viaja al agente al inicio de cada mensaje (no se
   *  muestra en la burbuja local). Ej.: el editor del sitio manda la página y la
   *  sección elegidas. undefined = el texto tal cual (como siempre). */
  messagePrefix?: string;
  /** Avisa al terminar cada turno del agente (p. ej. para recargar una vista previa). */
  onTurnDone?: () => void;
  /** 📎 Adjuntar archivos (clip, arrastrar y pegar). Activo por defecto. */
  attachments?: boolean;
  /** 'page' (pantalla completa, como siempre) · 'dock' (chat flotante compacto:
   *  cabecera propia con título editable, Ampliar y Minimizar/cerrar). */
  variant?: 'page' | 'dock';
  /** Publica el hilo como «hilo activo» compartido (flotante ↔ pantalla completa). */
  shareThread?: boolean;
  /** dock: título de respaldo de la cabecera (sin título del hilo). */
  dockTitle?: string;
  /** dock: elemento a la izquierda del título (orbe, avatar). */
  dockLeading?: React.ReactNode;
  /** dock: contenido bajo la cabecera (p. ej. el chip «Ver Leads» del orbe). */
  dockBelowHeader?: React.ReactNode;
  /** dock: «Ampliar» → abrir esta conversación a pantalla completa. */
  onExpand?: (threadId: string | null) => void;
  /** dock: «Minimizar» → volver al botón flotante (la conversación se conserva). */
  onMinimize?: () => void;
  /** Copiar / Regenerar bajo cada respuesta (default on). El diagnóstico lo apaga. */
  messageActions?: boolean;
  /** 🔧 Panel «pasos con herramientas» del turno (default on). El razonamiento
   *  que va en el mismo panel sigue `hideThinking`. El diagnóstico lo apaga. */
  showSteps?: boolean;
}

// Lista de modelos de la org: una sola petición por sesión de página (el
// ChatPane se re-monta en cada «Nuevo chat»). null = API no disponible.
// Por ORG (la del token): cambiar de org no reusa la lista de otra (p. ej. una
// comercial ofreciendo sus modelos dentro de una org soberana).
let _modelsCache: { org: string; at: number; p: Promise<ChatModelsInfo | null> } | null = null;
function loadChatModels(): Promise<ChatModelsInfo | null> {
  const org = orgIdFromToken() || '';
  if (!_modelsCache || _modelsCache.org !== org || Date.now() - _modelsCache.at > 5 * 60_000) {
    _modelsCache = { org, at: Date.now(), p: getChatModels() };
  }
  return _modelsCache.p;
}

const AUTO_CHOICE: ChatModelChoice = { model: 'auto', effort: null };

async function errorDetail(res: Response): Promise<string | null> {
  try {
    const j = await res.json();
    return typeof j?.detail === 'string' ? j.detail : null;
  } catch {
    return null;
  }
}

// Iniciadores por defecto del asistente general. Cada pantalla puede pasar los
// suyos con la prop `suggestions`: en el diagnóstico operativo, "Resume mis
// pendientes de hoy" o "Explícame cómo funciona esta plataforma" desorientan —
// la persona viene a que le pregunten por su trabajo, no a pedirle cosas al bot.
const SUGGESTIONS = [
  '¿Qué puedes hacer por mí?',
  'Resume mis pendientes de hoy',
  'Ayúdame a redactar un mensaje',
  'Explícame cómo funciona esta plataforma',
];

function makeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function orgId(): string {
  return localStorage.getItem('org_id') || '';
}

// 🛡️ Desenvuelve el JSON crudo del modelo si por una respuesta truncada/malformada
// se coló como contenido ({ "modo_respuesta":..., "bloques":[{...}] }). Muestra el
// texto real, nunca el envelope. Cubre también mensajes VIEJOS ya guardados en el
// hilo (generados antes del fix del backend). Aguanta JSON truncado (regex fallback).
function unwrapEnvelope(s: string | null | undefined): string {
  if (!s) return '';
  const t = s.trimStart();
  if (!t.startsWith('{') || !/"(modo_respuesta|bloques|contenido)"/.test(t.slice(0, 80))) return s;
  try {
    const obj: any = JSON.parse(t);
    const blo = obj?.bloques;
    if (Array.isArray(blo)) {
      const txt = blo
        .filter((b: any) => b?.tipo === 'texto')
        .map((b: any) => b?.contenido || '')
        .join('\n\n');
      if (txt) return txt;
    }
    if (typeof obj?.respuesta === 'string') return obj.respuesta;
  } catch {
    const parts = Array.from(t.matchAll(/"contenido"\s*:\s*"((?:[^"\\]|\\.)*)"?/g)).map(
      (m) => m[1],
    );
    if (parts.length) {
      return parts
        .map((p) =>
          p
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
            .replace(/\\"/g, '"')
            .replace(/\\\\/g, '\\'),
        )
        .join('\n\n');
    }
  }
  return s;
}

// ♿ Texto para el lector de pantalla: sin marcas de Markdown (**, #, `, viñetas).
function plainForSpeech(s: string): string {
  return s
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[*_`#>]+/g, '')
    .replace(/^\s*[-+]\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
}

// 🧠 «Proceso» del turno: pasos con herramientas (eventos `status`) + razonamiento
// profundo. Abierto mientras el agente trabaja; cerrado por defecto al terminar
// (estilo ChatGPT, discreto). Pasos: prop `showSteps`; razonamiento: `hideThinking`.
function ProcessPanel({ thinking, steps = [], live = false }: { thinking?: string | null; steps?: string[]; live?: boolean }) {
  const n = steps.length;
  const pasos = `${n} ${n === 1 ? 'paso' : 'pasos'}`;
  const title = live ? 'Trabajando…' : thinking && n ? `Razonamiento · ${pasos}` : thinking ? 'Razonamiento' : `${pasos} con herramientas`;
  return (
    <details open={live || undefined} className="group -mx-1 mb-1 rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-xs">
      <summary className="flex cursor-pointer select-none items-center gap-1.5 rounded font-medium text-muted-foreground marker:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        {thinking ? <Brain size={13} className="text-primary" aria-hidden /> : <Wrench size={13} className="text-primary" aria-hidden />}
        {title}
        <ChevronDown size={13} aria-hidden className="ml-auto motion-safe:transition-transform group-open:rotate-180" />
      </summary>
      {n > 0 && (
        <ol className="mt-2 space-y-1 text-muted-foreground">
          {steps.map((st, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span aria-hidden className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60 ${live && i === n - 1 ? 'motion-safe:animate-pulse' : ''}`} />
              <span>{st}</span>
            </li>
          ))}
        </ol>
      )}
      {thinking && <MessageContent text={thinking} muted className="mt-2" />}
    </details>
  );
}

function TextoView({ contenido, live = false }: { contenido: string; live?: boolean }) {
  return <MessageContent text={unwrapEnvelope(contenido)} live={live} />;
}

const renderTexto = (t: string) => <TextoView contenido={t} />;

function BloqueView({ bloque, live = false }: { bloque: Bloque; live?: boolean }) {
  const { tipo, contenido } = bloque;
  // 📊 Bloque visual (tabla, gráfica, KPI, organigrama…): valida en el borde y, si
  // no valida, cae al `contenido` como texto. Igual por stream (`bloque`) y por JSON.
  if (tipo === 'visual' && bloque.visual) return <VisualBloque bloque={bloque} renderFallback={renderTexto} />;
  // 🧭 Propuesta del Configurador de diagnósticos (ADR-007 §6): tarjeta con «Crear
  // diagnóstico». Si no valida, cae al resumen en texto (`contenido`).
  if (tipo === 'propuesta_diagnostico') return <PropuestaDiagnosticoBloque bloque={bloque} renderFallback={renderTexto} />;
  if (!contenido) return null;
  switch (tipo) {
    case 'imagen':
      return (
        <div>
          <img src={contenido} alt="adjunto" className="max-w-full rounded-lg border border-border" />
          <MediaSave url={contenido} tipo="imagen" />
        </div>
      );
    case 'video':
      return (
        <div>
          <video src={contenido} controls playsInline className="max-w-full rounded-lg" />
          <MediaSave url={contenido} tipo="video" />
        </div>
      );
    case 'audio':
      return (
        <div>
          <audio src={contenido} controls className="w-full" />
          <MediaSave url={contenido} tipo="audio" />
        </div>
      );
    case 'pdf':
      return (
        <a href={contenido} target="_blank" rel="noreferrer" className="text-primary underline break-all">
          Ver documento (PDF)
        </a>
      );
    default:
      return <TextoView contenido={contenido} live={live} />;
  }
}

// Mensaje de la persona: los tags de adjunto (que el agente necesita) se pintan
// como miniatura/tarjeta; el resto del texto, como siempre.
function UserBloqueView({ bloque }: { bloque: Bloque }) {
  if (bloque.tipo !== 'texto' || !bloque.contenido) return <BloqueView bloque={bloque} />;
  const { adjuntos, resto } = splitAttachments(bloque.contenido);
  if (!adjuntos.length) return <BloqueView bloque={bloque} />;
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {adjuntos.map((a) => (
          <AttachmentCard key={`${a.clase}-${a.refId}`} bloque={a} />
        ))}
      </div>
      {resto && <TextoView contenido={resto} />}
    </>
  );
}

// Bloques internos que el chat agrega al mensaje para el agente (menciones, contexto
// del editor…): el agente los necesita, el usuario no debe verlos en su burbuja.
function stripInternal(t: string): string {
  return t.replace(/<menciones_del_usuario>[\s\S]*?<\/menciones_del_usuario>/g, '')
    .replace(/^\[(?:Editor del sitio|Contexto)[^\]\n]{0,400}\]\s*/, '')
    .trim();
}

export function ChatPane({ threadId = null, startFresh = false, webChatHeaders = {}, onThreadStarted, hideThinking = false, suggestions, emptyTitle, emptySubtitle, focusTools, assistantAvatarUrl, assistantId, hideAgentSelector = false, suggestionItems, modelPicker = false, onThreadInfo, onTitlePending, mentions = false, messagePrefix, onTurnDone, onUserSend, attachments = true, variant = 'page', shareThread = false, dockTitle = 'Asistente', dockLeading, dockBelowHeader, onExpand, onMinimize, messageActions = true, showSteps = true }: ChatPaneProps) {
  const dock = variant === 'dock';
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  // 📎 Adjuntar (misma API que WebChat, ver ./attachments.tsx): el archivo se sube
  // al elegirlo y queda PENDIENTE como miniatura; su tag (con el id) se antepone
  // al texto al enviar. NO se auto-envía: la persona decide qué pedir.
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const attach = useCallback(async (file: File | null | undefined) => {
    if (!attachments || !file || uploading) return;
    setError(null);
    setUploading(true);
    try {
      const p = await uploadChatAttachment(file);
      setPending((prev) => [...prev, p]);
      taRef.current?.focus();
    } catch (e: any) {
      setError(e?.message || 'No se pudo subir el archivo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [attachments, uploading]);
  const removePending = useCallback((id: string) => {
    setPending((prev) => {
      const v = prev.find((a) => a.id === id);
      if (v?.preview) URL.revokeObjectURL(v.preview);
      return prev.filter((a) => a.id !== id);
    });
  }, []);
  // Los objectURL viven hasta revocarse: adjuntar y cerrar sin enviar los retendría.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  useEffect(() => () => pendingRef.current.forEach((a) => a.preview && URL.revokeObjectURL(a.preview)), []);
  // 🎙️ Dictado por voz (Web Speech API del navegador, es-MX). Sin soporte → sin botón.
  const recRef = useRef<any>(null);
  const [listening, setListening] = useState(false);
  const voiceOk = typeof window !== 'undefined' && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  const toggleVoice = useCallback(() => {
    if (listening) { recRef.current?.stop(); return; }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = 'es-MX'; rec.interimResults = true; rec.continuous = false;
    const base = input ? input.trimEnd() + ' ' : '';
    rec.onresult = (ev: any) => {
      let txt = '';
      for (let i = 0; i < ev.results.length; i++) txt += ev.results[i][0].transcript;
      setInput(base + txt);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec; setListening(true); rec.start();
  }, [listening, input]);
  const [activeThread, setActiveThread] = useState<string | null>(threadId);
  // 🔁 Hilo activo compartido (flotante ↔ pantalla completa): ver ./activeThread.ts.
  useEffect(() => {
    if (shareThread && activeThread) shareActiveThread(activeThread);
  }, [shareThread, activeThread]);
  // 🏷️ Título del hilo en la cabecera del flotante (las páginas pintan el suyo).
  const [ownTitle, setOwnTitle] = useState<string | null>(null);
  const titleTimers = useRef<number[]>([]);
  useEffect(() => () => titleTimers.current.forEach((t) => window.clearTimeout(t)), []);
  const refreshOwnTitle = useCallback(async (tid: string) => {
    try {
      const data = await getMyThreads();
      const t = (data?.threads || []).find((x: any) => (x.id || x.thread_id || x.session_id) === tid);
      if (t?.title) setOwnTitle(String(t.title));
    } catch {
      /* sin lista: se queda el título de respaldo */
    }
  }, []);
  useEffect(() => {
    if (!dock) return;
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ id: string; title: string }>).detail;
      if (d?.id && d.id === activeThread) setOwnTitle(d.title);
    };
    window.addEventListener('orion-thread-renamed', on);
    return () => window.removeEventListener('orion-thread-renamed', on);
  }, [dock, activeThread]);
  const renameOwn = async (title: string) => {
    if (!activeThread) return;
    const prev = ownTitle;
    setOwnTitle(title);
    try {
      await renameThread(activeThread, title);
      window.dispatchEvent(new CustomEvent('orion-thread-renamed', { detail: { id: activeThread, title } }));
    } catch {
      setOwnTitle(prev);
      setError('No se pudo renombrar la conversación');
    }
  };
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 🧠 Thinking toggle: OFF por defecto (respuesta rápida). ON = razonamiento
  // profundo del modelo soberano U-CogNet (~30-120s, panel colapsable).
  const [think, setThink] = useState(false);
  // 🪞 Modo "U-CogNet sobre sí mismo": habla con la memoria gobernada del propio
  // código de U-CogNet (grounded + firmado). Solo se ofrece a la org ucognet; el
  // backend lo gatea igual (self_knowledge AND org_id=="ucognet").
  const [selfKnowledge, setSelfKnowledge] = useState(false);
  // 🤖 Agente personalizado activo (null = asistente genérico). Se manda como
  // assistant_id al backend, que resuelve su receta e inserta la capa <agent>.
  const [agents, setAgents] = useState<AssistantOut[]>([]);
  // `?agente=<id>` (botón «Probar en el chat» de la app de Agentes) lo preselecciona;
  // si no está entre los agentes que ve el usuario, se descarta al cargar la lista.
  const [ownAssistantId, setActiveAssistantId] = useState<string>(() => {
    try { return new URLSearchParams(window.location.search).get('agente') || ''; } catch { return ''; }
  });
  const activeAssistantId = assistantId ?? ownAssistantId;
  // 🎛️ Modelo/esfuerzo de ESTA conversación («Auto» = lo que decide la org).
  const [modelsInfo, setModelsInfo] = useState<ChatModelsInfo | null>(null);
  const [choice, setChoice] = useState<ChatModelChoice>(AUTO_CHOICE);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  // 🔒 El modelo elegido (o el guardado en el hilo) SIEMPRE debe estar en la lista
  // que la org permite; si no (hilo viejo, org soberana, llave retirada) → Auto.
  useEffect(() => {
    if (!modelsLoaded || !choice.model || choice.model === 'auto') return;
    const m = modelsInfo?.models.find((x) => x.id === choice.model);
    if (!m || !modelsInfo?.enabled) { setChoice(AUTO_CHOICE); return; }
    if (choice.effort && !m.efforts.some((e) => e.id === choice.effort)) {
      setChoice({ model: m.id, effort: m.efforts.length ? m.default_effort : null });
    }
  }, [modelsLoaded, modelsInfo, choice]);
  // Soberana con un solo modelo gobernado: no hay nada que elegir → sin selector
  // («Auto» ya resuelve al modelo gobernado; la etiqueta del shell lo informa).
  const pickerOn = modelPicker && !!modelsInfo?.enabled && modelsInfo.models.length > (modelsInfo.sovereign ? 1 : 0);

  // 📡 Respuesta en tiempo real (SSE): línea de estado del agente y texto en vivo.
  // El texto se acumula en un ref y se pinta UNA vez por frame (no por token).
  const [agentStatus, setAgentStatus] = useState<string | null>(null);
  const [liveText, setLiveText] = useState('');
  // Bloques ricos (p. ej. visuales) que llegan por SSE antes del `done`.
  const [liveBloques, setLiveBloques] = useState<Bloque[]>([]);
  // 🔧 Pasos del turno en curso («Consultando leads…»); se guardan en la respuesta.
  const [liveSteps, setLiveSteps] = useState<string[]>([]);
  const stepsRef = useRef<string[]>([]);
  const [streaming, setStreaming] = useState(false);
  // ⏳ El hilo se abrió con un turno aún en curso (se minimizó/amplió o se cambió de
  // pantalla a media respuesta): se espera la respuesta guardada en vez de mostrar
  // la pregunta sola.
  const [awaiting, setAwaiting] = useState(false);
  const [announce, setAnnounce] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const liveRef = useRef('');
  const rafRef = useRef<number | null>(null);

  const endRef = useRef<HTMLDivElement | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  const flushLive = useCallback((now = false) => {
    if (now) {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setLiveText(liveRef.current);
      return;
    }
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setLiveText(liveRef.current);
    });
  }, []);

  // 📝 GFM (tablas, tareas…) se descarga aparte: se pide en cuanto se abre el chat.
  useEffect(() => {
    preloadRichText().catch(() => undefined);
  }, []);

  // Al desmontar (minimizar al orbe, ampliar, cambiar de pantalla) el stream se
  // SUELTA, no se corta: cortar la conexión detiene el turno en el backend y la
  // respuesta se perdía (29-oct, municipio). El turno termina y se guarda; quien
  // abra el hilo la recoge (ver `awaiting`). Solo «Detener» corta de verdad.
  useEffect(
    () => () => {
      abortRef.current = null;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  // 🚨 2026-07-27 · Sesión vencida al enviar/iniciar. ChatPane hace su propio
  // fetch (no pasa por lib/api.ts), así que ANTES un 401 sólo mostraba
  // "Error al enviar (401)" y dejaba al usuario atorado sin salida — justo a
  // media entrevista. Ahora, igual que el resto del app: limpia el token y
  // dispara auth:logout para que la App re-autentique. Devuelve true si era 401.
  const manejo401 = useCallback((status: number): boolean => {
    if (status !== 401) return false;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('org_id');
    window.dispatchEvent(new Event('auth:logout'));
    setError('Tu sesión expiró. Vuelve a iniciar sesión para continuar — tu avance quedó guardado.');
    return true;
  }, []);

  const buildHeaders = useCallback(
    (): Record<string, string> => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
      ...webChatHeaders,
    }),
    [webChatHeaders],
  );

  useEffect(() => {
    // 🚨 2026-07-24 · `block: 'nearest'` es OBLIGATORIO.
    // Sin él, scrollIntoView desplaza TODOS los contenedores scrollables
    // ancestros —incluida la página— así que al auto-scrollear al último
    // mensaje se arrastraba la ventana entera hacia abajo y el encabezado de
    // la página quedaba fuera de vista: el usuario tenía que subir con la
    // barra para leerlo. Con 'nearest' el scroll ocurre SOLO dentro del
    // contenedor de mensajes.
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, loading]);

  // 🤖 Carga los agentes disponibles para el selector (best-effort: si falla,
  // el chat sigue con el asistente genérico).
  useEffect(() => {
    let cancelled = false;
    listAssistants('all')
      .then((list) => {
        if (cancelled) return;
        setAgents(list);
        setActiveAssistantId((cur) => (cur && !list.some((a) => a.id === cur) ? '' : cur));
      })
      .catch(() => {
        /* sin agentes → solo genérico */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!modelPicker) return;
    let cancelled = false;
    void loadChatModels().then((info) => {
      // Org soberana: solo el modelo gobernado (defensa en profundidad sobre el backend).
      if (cancelled) return;
      setModelsInfo(info && info.sovereign ? sovereignOnly(info) : info);
      setModelsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [modelPicker]);

  const activeAgent = agents.find((a) => a.id === activeAssistantId) || null;

  // 🧩 Menciones del compositor: @agente (persiste hasta quitarlo), /skill y
  // #herramienta (solo el siguiente mensaje). Ver ./mentions.ts.
  const menuId = useId();
  const [narrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches);
  // Con agente controlado desde fuera (o selector oculto), @ no cambia el agente.
  const agentCtl = assistantId === undefined && !hideAgentSelector;
  const [trigger, setTrigger] = useState<{ kind: MentionKind; query: string; start: number } | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [skill, setSkill] = useState<MentionItem | null>(null);
  const [pickedTools, setPickedTools] = useState<MentionItem[]>([]);
  const [lists, setLists] = useState<{ skill: MentionItem[] | null; tool: MentionItem[] | null }>({ skill: null, tool: null });
  const agentItems: MentionItem[] = agents.map((a) => ({
    kind: 'agent', id: a.id, label: a.name, description: a.description, icon: a.icon || undefined,
  }));
  const activeAgentItem: MentionItem | null = activeAgent
    ? { kind: 'agent', id: activeAgent.id, label: activeAgent.name, icon: activeAgent.icon || undefined }
    : null;

  useEffect(() => {
    if (!trigger) return;
    if (trigger.kind === 'skill' && lists.skill === null) {
      void loadSkills().then((l) => setLists((x) => ({ ...x, skill: l })));
    } else if (trigger.kind === 'tool' && lists.tool === null) {
      void loadTools(buildHeaders(), focusTools).then((l) => setLists((x) => ({ ...x, tool: l })));
    }
  }, [trigger, lists, buildHeaders, focusTools]);

  const sourceFor = (k: MentionKind): MentionItem[] | null =>
    k === 'agent' ? agentItems : k === 'skill' ? lists.skill : lists.tool;
  const menuOpen = !!trigger && (trigger.kind !== 'agent' || agentCtl);
  const menuItems = menuOpen && trigger
    ? filterItems(
        (sourceFor(trigger.kind) || []).filter((it) => !(it.kind === 'tool' && pickedTools.some((p) => p.id === it.id))),
        trigger.query,
      )
    : [];
  const menuLoading = menuOpen && !!trigger && sourceFor(trigger.kind) === null;

  const closeMenu = () => {
    setTrigger(null);
    setActiveIdx(0);
  };

  const pickMention = (it: MentionItem) => {
    const el = taRef.current;
    if (trigger && el) {
      const caret = el.selectionStart ?? input.length;
      const next = (input.slice(0, trigger.start) + input.slice(caret)).replace(/\s{2,}/g, ' ');
      setInput(next.trimStart() ? next : '');
      requestAnimationFrame(() => {
        el.focus();
        const pos = Math.min(trigger.start, next.length);
        el.setSelectionRange(pos, pos);
      });
    }
    if (it.kind === 'agent') setActiveAssistantId(it.id);
    else if (it.kind === 'skill') setSkill(it);
    else setPickedTools((l) => (l.some((x) => x.id === it.id) || l.length >= MAX_TOOLS ? l : [...l, it]));
    closeMenu();
  };

  const updateTrigger = (text: string, caret: number) => {
    const t = mentions ? activeTrigger(text, caret) : null;
    if (!t) {
      if (trigger) closeMenu();
      return;
    }
    if (!trigger || trigger.kind !== t.kind || trigger.query !== t.query) setActiveIdx(0);
    setTrigger(t);
  };

  const loadMessages = useCallback(
    async (tid: string) => {
      const res = await fetch(apiUrl(`/threads/${tid}/messages`), { headers: buildHeaders() });
      if (!res.ok) {
        // Antes se ignoraba y el chat quedaba en blanco sin explicación.
        if (manejo401(res.status)) return;
        throw new Error(
          res.status === 404
            ? 'No encontré esta conversación. Puede que se haya borrado o que no sea de tu organización.'
            : res.status === 403
              ? 'Esta conversación no es tuya.'
              : `No se pudo abrir la conversación (${res.status}).`,
        );
      }
      const data = await res.json().catch(() => null);
      const list: ChatMsg[] = (data?.messages || []).map((m: any) => ({
        id: m.id || makeId(),
        role: m.role === 'assistant' ? 'assistant' : 'user',
        bloques: (Array.isArray(m.bloques) ? m.bloques : []).map((b: any) =>
          m.role !== 'assistant' && b?.tipo === 'texto' && typeof b.contenido === 'string'
            ? { ...b, contenido: stripInternal(b.contenido) }
            : b),
        thinking: m.thinking || null,
        steps: Array.isArray(m.steps) ? m.steps.filter((x: unknown) => typeof x === 'string') : undefined,
      }));
      setMessages(list);
      // Título y modelo guardados del hilo (backend nuevo; el viejo no los manda).
      const info = data?.thread;
      if (info) {
        const cm = info.chat_model;
        // Se valida contra la lista de la org en el efecto de abajo (si no está → Auto).
        if (cm?.model && modelPicker) setChoice({ model: String(cm.model), effort: cm.effort ?? null });
        onThreadInfo?.(tid, { title: info.title ?? null, titleSource: info.title_source ?? null });
        setOwnTitle(info.title ?? null);
      }
    },
    [buildHeaders, onThreadInfo, manejo401, modelPicker],
  );

  // Recoge la respuesta de un turno que siguió corriendo sin esta pantalla.
  useEffect(() => {
    if (!awaiting || !activeThread) return;
    const turno = turnosEnCurso.get(activeThread);
    let alive = true;
    const fin = () => {
      if (!alive) return;
      loadMessages(activeThread).catch(() => undefined).finally(() => { if (alive) setAwaiting(false); });
    };
    if (!turno) { fin(); return () => { alive = false; }; }
    turno.then(fin, fin);
    return () => { alive = false; };
  }, [awaiting, activeThread, loadMessages]);

  // Resuelve el hilo cuando cambia el prop threadId (nuevo chat o reabrir historial).
  useEffect(() => {
    let cancelled = false;
    // Se suelta el turno del hilo anterior (sigue y se guarda allá) y se limpia su UI.
    abortRef.current = null;
    liveRef.current = '';
    setLiveText('');
    setLiveBloques([]);
    setLiveSteps([]);
    setAgentStatus(null);
    setStreaming(false);
    setLoading(false);
    setAwaiting(false);
    setBooting(true);
    setError(null);
    setMessages([]);
    setOwnTitle(null);
    (async () => {
      try {
        if (threadId) {
          // Reabrir un hilo existente.
          if (cancelled) return;
          setActiveThread(threadId);
          // Se mira ANTES de cargar: si el turno termina durante la carga, el efecto
          // de `awaiting` no halla la promesa y recarga una vez.
          const enCurso = turnosEnCurso.has(threadId);
          await loadMessages(threadId);
          if (!cancelled && enCurso) setAwaiting(true);
        } else {
          // Iniciar/recuperar el hilo del usuario.
          const res = await fetch(apiUrl('/threads/start'), {
            method: 'POST',
            headers: buildHeaders(),
            // new_thread:true → el backend CREA un thread nuevo (no recupera el
            // determinístico del usuario). Solo cuando viene de "Nuevo chat".
            body: JSON.stringify({ site_id: orgId(), visitor_id: 'dashboard-ui', nombre: 'Usuario', new_thread: startFresh || undefined }),
          });
          if (!res.ok) {
            if (manejo401(res.status)) return;
            throw new Error(`No se pudo iniciar el chat (${res.status})`);
          }
          const data = await res.json();
          if (!data?.thread_id) throw new Error('Respuesta inválida: falta thread_id');
          if (cancelled) return;
          setActiveThread(data.thread_id);
          onThreadStarted?.(data.thread_id);
          // Se mira ANTES de cargar: si el turno termina durante la carga, el efecto
          // de `awaiting` no halla la promesa y recarga una vez.
          const enCurso = turnosEnCurso.has(data.thread_id);
          await loadMessages(data.thread_id);
          if (!cancelled && enCurso) setAwaiting(true);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'No se pudo cargar la conversación');
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const send = useCallback(
    async (textArg?: string) => {
      // Los adjuntos solo viajan con lo que la persona escribió; una sugerencia
      // (textArg) no arrastra lo que quedó en el compositor.
      const sentPending = textArg === undefined ? pending : [];
      const typed = (textArg ?? input).trim();
      const text = (sentPending.map((a) => a.tag).join('') + typed).trim();
      if (!text || loading || awaiting || !activeThread || uploading) return;
      try { if (typed) onUserSend?.(typed); } catch { /* nunca bloquea el envío */ }
      if (sentPending.length) {
        sentPending.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
        setPending([]);
      }

      setError(null);
      setLoading(true);

      const sentMentions: MentionItem[] = [
        ...(activeAgentItem ? [activeAgentItem] : []),
        ...(skill ? [skill] : []),
        ...pickedTools,
      ];
      const sentAgent = activeAgentItem;
      const userMsg: ChatMsg = {
        id: makeId(),
        role: 'user',
        bloques: [{ tipo: 'texto', contenido: text }],
        mentions: sentMentions.length ? sentMentions : undefined,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      closeMenu();
      // /skill y #herramienta valen para ESTE mensaje; el @agente se queda.
      setSkill(null);
      setPickedTools([]);
      if (taRef.current) taRef.current.style.height = 'auto';

      const payload = {
        role: 'user',
        bloques: [{ tipo: 'texto', contenido: messagePrefix ? `${messagePrefix}\n\n${text}` : text }],
        site_id: orgId(),
        visitor_id: 'dashboard-ui',
        nombre: 'Usuario',
        think, // 🧠 razonamiento profundo si el toggle está ON
        assistant_id: activeAssistantId || undefined, // 🤖 agente activo (capa <agent>)
        focus_tools: focusTools, // 🎯 acota el catálogo de tools al módulo
        self_knowledge: selfKnowledge || undefined, // 🪞 modo "U-CogNet sobre sí mismo"
        skill: skill?.id, // 🧩 /skill → el agente abre la receta con get_workflow
        focus_tool_ids: pickedTools.length ? pickedTools.map((t) => t.id) : undefined, // 🧩 #herramienta
        // 🎛️ Modelo/esfuerzo del hilo: solo si la org ofrece el selector.
        ...(pickerOn
          ? { model: choice.model, effort: choice.model !== 'auto' ? choice.effort || undefined : undefined }
          : {}),
        // 📱 En qué pantalla estaba la persona (ruta sin secretos, modo pwa/navegador,
        // dispositivo, viewport, versión). Si levanta un ticket, Orión lo adjunta como
        // evidencia; si lo ignora, no pasa nada (campo opcional).
        pantalla: capturarPantalla(),
      };

      // Misma forma de respuesta para el endpoint JSON y el `done` del stream.
      type TurnResponse = {
        message?: { id?: string; role?: string; bloques?: Bloque[]; thinking?: string | null };
        bloques?: Bloque[];
        thread?: { title_pending?: boolean };
      } | null;
      const applyResponse = async (json: TurnResponse) => {
        if (json?.thread?.title_pending) {
          onTitlePending?.(activeThread);
          if (dock) [2500, 7000].forEach((ms) => titleTimers.current.push(window.setTimeout(() => void refreshOwnTitle(activeThread), ms)));
        }
        if (json?.message?.role === 'assistant') {
          const msg = json.message;
          const bloques: Bloque[] = Array.isArray(msg.bloques) ? msg.bloques : [];
          setMessages((prev) => [
            ...prev,
            {
              id: msg.id || makeId(),
              role: 'assistant',
              bloques,
              thinking: msg.thinking || null,
              agent: sentAgent,
              steps: stepsRef.current.length ? stepsRef.current : undefined,
            },
          ]);
          // ♿ Se anuncia la respuesta FINAL una vez (no cada token).
          setAnnounce(
            plainForSpeech(
              bloques
                .filter((b) => TEXTUALES.has(b.tipo))
                .map((b) => unwrapEnvelope(b.contenido))
                .join('\n\n'),
            ),
          );
        } else if (Array.isArray(json?.bloques)) {
          const bl = json.bloques;
          const steps = stepsRef.current.length ? stepsRef.current : undefined;
          setMessages((prev) => [...prev, { id: makeId(), role: 'assistant', bloques: bl, agent: sentAgent, steps }]);
        } else {
          await loadMessages(activeThread);
        }
      };

      const sendJson = async () => {
        const res = await fetch(apiUrl(`/threads/${activeThread}/messages`), {
          method: 'POST',
          headers: buildHeaders(),
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          if (manejo401(res.status)) return;
          const detail = res.status === 422 || res.status === 403 ? await errorDetail(res) : null;
          throw new Error(detail || `Error al enviar (${res.status})`);
        }
        await applyResponse(await res.json().catch(() => null));
      };

      const onEvent = (ev: StreamEvent) => {
        // Turno soltado (cambio de hilo / desmontaje): sus eventos ya no son de esta vista.
        if (abortRef.current !== ctrl) return;
        if (ev.type === 'status') {
          if (ev.label) {
            setAgentStatus(ev.label);
            // «Pensando…» no es un paso; las repeticiones seguidas tampoco.
            const last = stepsRef.current[stepsRef.current.length - 1];
            if (!/^pensando/i.test(ev.label) && ev.label !== last) {
              stepsRef.current = [...stepsRef.current, ev.label];
              setLiveSteps(stepsRef.current);
            }
          }
        } else if (ev.type === 'delta') {
          liveRef.current += ev.text || '';
          flushLive();
        } else if (ev.type === 'reset') {
          // Lo escrito era el preámbulo de una vuelta con herramientas.
          liveRef.current = '';
          flushLive(true);
        }
        else if (ev.type === 'bloque') {
          // Un bloque visual se pinta en cuanto llega; el `done` trae todos y reemplaza.
          const b = ev.bloque as Bloque | undefined;
          if (b && b.tipo === 'visual') setLiveBloques((prev) => [...prev, b]);
        }
      };

      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const hiloDelTurno = activeThread;
      let liberar: () => void = () => undefined;
      const turno = new Promise<void>((r) => { liberar = r; });
      turnosEnCurso.set(hiloDelTurno, turno);
      liveRef.current = '';
      setLiveText('');
      setLiveBloques([]);
      stepsRef.current = [];
      setLiveSteps([]);
      setAgentStatus('Pensando…');
      setStreaming(true);
      try {
        let outcome;
        try {
          outcome = await streamThreadMessage({
            url: apiUrl(`/threads/${activeThread}/messages/stream`),
            headers: buildHeaders(),
            body: payload,
            signal: ctrl.signal,
            onEvent,
          });
        } catch (e) {
          if (e instanceof StreamHttpError) {
            if (manejo401(e.status)) return;
            throw new Error(e.detail || `Error al enviar (${e.status})`);
          }
          throw e;
        }
        if (abortRef.current !== ctrl) return; // soltado: la respuesta se guarda en su hilo
        if (outcome.kind === 'done') {
          await applyResponse(outcome.done);
        } else if (outcome.kind === 'aborted') {
          // Detenido por la persona: el backend no guarda la respuesta parcial.
          const parcial = liveRef.current.trim();
          if (parcial) {
            setMessages((prev) => [
              ...prev,
              { id: makeId(), role: 'assistant', bloques: [{ tipo: 'texto', contenido: parcial }], stopped: true, agent: sentAgent, steps: stepsRef.current.length ? stepsRef.current : undefined },
            ]);
          }
          setAnnounce('Respuesta detenida.');
        } else {
          // El stream no está disponible (backend viejo, proxy…): JSON de siempre.
          setStreaming(false);
          await sendJson();
        }
      } catch (e) {
        if (abortRef.current === ctrl) setError((e instanceof Error && e.message) || 'Error al enviar el mensaje');
      } finally {
        if (turnosEnCurso.get(hiloDelTurno) === turno) turnosEnCurso.delete(hiloDelTurno);
        liberar();
        // Soltado → esta vista ya es de otro hilo (o se desmontó): no se toca su estado.
        if (abortRef.current !== ctrl) return;
        abortRef.current = null;
        setLiveBloques([]);
        setLiveSteps([]);
        liveRef.current = '';
        flushLive(true);
        setAgentStatus(null);
        setStreaming(false);
        setLoading(false);
        onTurnDone?.();
      }
    },
    [input, loading, activeThread, buildHeaders, loadMessages, think, activeAssistantId, selfKnowledge, pickerOn, choice, onTitlePending, focusTools, manejo401, flushLive, activeAgentItem, skill, pickedTools, messagePrefix, onTurnDone, onUserSend, pending, uploading, dock, refreshOwnTitle, awaiting],
  );

  // 🔁 Regenerar: vuelve a mandar el último mensaje de la persona por el MISMO `send`
  // (stream, JSON de respaldo, modelo, agente…). El backend no reemplaza turnos: la
  // respuesta nueva queda debajo, como un mensaje más.
  const regenerate = useCallback(
    (idx: number) => {
      for (let i = idx - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.role !== 'user') continue;
        const t = m.bloques.filter((b) => b.tipo === 'texto').map((b) => b.contenido).join('\n\n').trim();
        if (t) void send(t);
        return;
      }
    },
    [messages, send],
  );
  const lastAssistantIdx = messages.reduce((acc, m, i) => (m.role === 'assistant' ? i : acc), -1);

  // ⏹️ Detener: corta el stream; el backend detiene el turno y no guarda la parcial.
  const stop = useCallback(() => abortRef.current?.abort('detenido'), []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (menuOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const n = menuItems.length;
        if (n) setActiveIdx((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n));
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey && menuItems[activeIdx]) {
        e.preventDefault();
        pickMention(menuItems[activeIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        return;
      }
    }
    if (e.key === 'Backspace' && !input && taRef.current?.selectionStart === 0) {
      // Borrar con el compositor vacío quita la última mención (como un chip).
      if (pickedTools.length) return setPickedTools((l) => l.slice(0, -1));
      if (skill) return setSkill(null);
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const onInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    updateTrigger(e.target.value, e.target.selectionStart ?? e.target.value.length);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const showEmpty = !booting && messages.length === 0;
  const canSend = (!!input.trim() || pending.length > 0) && !loading && !awaiting && !booting && !uploading;

  // 📎 Arrastrar o pegar un archivo: el gesto natural, sin buscar el clip.
  const dropHandlers = attachments
    ? {
        onDragOver: (e: React.DragEvent) => {
          if (!Array.from(e.dataTransfer.types || []).includes('Files')) return;
          // preventDefault obligatorio: sin él el navegador abre el archivo en otra pestaña.
          e.preventDefault();
          if (!dragOver) setDragOver(true);
        },
        onDragLeave: (e: React.DragEvent) => {
          // Pasar sobre un hijo dispara dragLeave: solo se apaga al salir de verdad.
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
        },
        onDrop: (e: React.DragEvent) => {
          if (!e.dataTransfer.files?.length) return;
          e.preventDefault();
          setDragOver(false);
          void attach(e.dataTransfer.files[0]);
        },
      }
    : {};
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!attachments) return;
    const f = e.clipboardData?.files?.[0];
    if (f) {
      e.preventDefault();
      void attach(f);
    }
  };

  const iconBtn =
    'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-60';
  const pill = (on: boolean) =>
    `flex h-10 shrink-0 items-center gap-1.5 rounded-xl border px-3 text-sm font-medium transition-colors disabled:opacity-60 ${
      on ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:bg-muted'
    }`;

  const thinkBtn = !hideThinking && (
    <button
      type="button"
      onClick={() => setThink((v) => !v)}
      disabled={booting}
      aria-pressed={think}
      aria-label="Pensar"
      title={think ? 'Razonamiento profundo activado (más lento, más preciso)' : 'Activar razonamiento profundo'}
      className={pill(think)}
    >
      <Brain size={16} />
      <span className={dock ? 'sr-only' : 'hidden sm:inline'}>Pensar</span>
    </button>
  );
  const selfBtn = orgId() === 'ucognet' && (
    <button
      type="button"
      onClick={() => setSelfKnowledge((v) => !v)}
      disabled={booting}
      aria-pressed={selfKnowledge}
      aria-label="Sobre mí"
      title={selfKnowledge ? 'U-CogNet responde desde su propio auto-conocimiento (grounded)' : 'Hablar con la memoria de U-CogNet sobre su propio código'}
      className={pill(selfKnowledge)}
    >
      <Sparkles size={16} />
      <span className={dock ? 'sr-only' : 'hidden sm:inline'}>Sobre mí</span>
    </button>
  );
  const modelBtn = pickerOn && modelsInfo && (
    <ModelPicker info={modelsInfo} value={choice} onChange={setChoice} disabled={booting} compact={dock} />
  );
  const clipBtn = attachments && (
    <>
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept={ATTACH_ACCEPT}
        onChange={(e) => void attach(e.target.files?.[0])}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={booting || uploading}
        aria-label="Adjuntar archivo"
        title="Adjuntar (Excel, CSV, PDF, imagen, docx…) · también puedes arrastrar o pegar"
        className={iconBtn}
      >
        {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
      </button>
    </>
  );
  const voiceBtn = voiceOk && !streaming && (
    <button type="button" onClick={toggleVoice} disabled={booting}
      aria-label={listening ? 'Detener dictado' : 'Dictar por voz'} aria-pressed={listening} title={listening ? 'Escuchando… (toca para terminar)' : 'Dictar por voz'}
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors disabled:opacity-60 ${listening ? 'border-primary bg-primary/10 text-primary motion-safe:animate-pulse' : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
      <Mic size={16} />
    </button>
  );
  const sendBtn = streaming ? (
    <button
      type="button"
      onClick={stop}
      aria-label="Detener respuesta"
      title="Detener respuesta"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-foreground transition-colors hover:bg-muted"
    >
      <Square size={14} className="fill-current" />
    </button>
  ) : (
    <button
      type="button"
      onClick={() => void send()}
      disabled={!canSend}
      aria-label="Enviar"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
    >
      <Send size={16} />
    </button>
  );
  const textarea = (
    <textarea
      ref={taRef}
      value={input}
      onChange={onInput}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      rows={1}
      placeholder={!mentions ? 'Escribe un mensaje…' : narrow || dock ? 'Mensaje · @ / #' : 'Escribe… (@ agente · / skill · # herramienta)'}
      disabled={booting}
      role={mentions ? 'combobox' : undefined}
      aria-autocomplete={mentions ? 'list' : undefined}
      aria-expanded={mentions ? menuOpen : undefined}
      aria-controls={menuOpen ? menuId : undefined}
      aria-activedescendant={menuOpen && menuItems[activeIdx] ? optionId(menuId, activeIdx) : undefined}
      aria-label="Mensaje"
      onBlur={() => window.setTimeout(closeMenu, 120)}
      onClick={(e) => updateTrigger(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)}
      className={`max-h-[200px] min-w-0 flex-1 resize-none rounded-xl border border-border bg-card text-base text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring disabled:opacity-60 sm:text-sm ${
        dock ? 'w-full px-3.5 py-2.5' : 'px-4 py-2.5'
      }`}
    />
  );

  const avatar = (size: number) =>
    assistantAvatarUrl ? (
      <img src={assistantAvatarUrl} alt="" style={{ width: size, height: size }} />
    ) : (
      <Bot size={size === 24 ? 16 : 24} className="text-muted-foreground" />
    );

  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      {dock && (
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          {dockLeading}
          <ThreadHeaderTitle
            as="h2"
            title={ownTitle}
            fallback={dockTitle}
            className="text-sm font-semibold text-foreground"
            onRename={activeThread ? renameOwn : undefined}
          />
          {onExpand && (
            <button
              type="button"
              onClick={() => onExpand(activeThread)}
              className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Ampliar a pantalla completa"
              title="Ampliar"
            >
              <Maximize2 size={16} />
            </button>
          )}
          {onMinimize && (
            <button
              type="button"
              onClick={onMinimize}
              className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Minimizar (Esc) · la conversación se conserva"
              title="Minimizar (Esc)"
            >
              <Minimize2 size={16} className="hidden sm:block" />
              <X size={18} className="sm:hidden" />
            </button>
          )}
        </div>
      )}
      {dock && dockBelowHeader}
      <div
        className={`relative flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] ${dragOver ? 'ring-2 ring-inset ring-primary/60' : ''}`}
        {...dropHandlers}
      >
        {dragOver && (
          <div className="pointer-events-none sticky top-2 z-10 mx-3 flex items-center justify-center gap-2 rounded-xl border border-dashed border-primary/60 bg-primary/10 py-3 text-sm text-foreground">
            <Paperclip size={16} aria-hidden />
            Suelta el archivo para que el agente lo cargue
          </div>
        )}
        <div className={`mx-auto flex max-w-3xl flex-col ${dock ? 'gap-4 px-3 py-4' : 'gap-5 px-3 py-5 sm:gap-6 sm:px-4 sm:py-6'}`}>
          {booting && <div className="text-center text-sm text-muted-foreground">Cargando…</div>}

          {showEmpty && (
            <div className={`flex flex-col items-center text-center ${dock ? 'gap-4 pt-6' : 'gap-6 pt-16'}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                {assistantAvatarUrl ? (
                  <img src={assistantAvatarUrl} alt="" className="h-9 w-9" />
                ) : (
                  <Bot size={24} className="text-primary" />
                )}
              </div>
              <div>
                <p className={`${dock ? 'text-base' : 'text-lg'} font-semibold`}>{emptyTitle ?? '¿En qué te ayudo?'}</p>
                <p className="text-sm text-muted-foreground">{emptySubtitle ?? 'Escribe tu pregunta o elige una sugerencia.'}</p>
              </div>
              {suggestionItems ? (
                <div className={`grid w-full gap-2.5 ${dock ? '' : 'sm:grid-cols-2'}`}>
                  {suggestionItems.map((it) => (
                    <button
                      key={it.text}
                      type="button"
                      onClick={() => void send(it.text)}
                      className="flex items-center gap-3 rounded-2xl border border-border bg-card px-3.5 py-3 text-left text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-muted"
                    >
                      {it.icon && (
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary" aria-hidden>
                          {it.icon}
                        </span>
                      )}
                      <span className="min-w-0 flex-1">{it.text}</span>
                    </button>
                  ))}
                </div>
              ) : (
              <div className={`grid w-full gap-2 ${dock ? '' : 'sm:grid-cols-2'}`}>
                {(suggestions ?? SUGGESTIONS).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void send(s)}
                    className="rounded-xl border border-border bg-card px-4 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted"
                  >
                    {s}
                  </button>
                ))}
              </div>
              )}
            </div>
          )}

          {messages.map((m, idx) => {
            const mine = m.role === 'user';
            const wide = !mine && isWide(m.bloques);
            const copyable = mine
              ? ''
              : m.bloques
                  .map((b) => (b.tipo === 'texto' ? unwrapEnvelope(b.contenido) : TEXTUALES.has(b.tipo) && typeof b.contenido === 'string' ? b.contenido : ''))
                  .filter(Boolean)
                  .join('\n\n');
            const canRegen = !mine && idx === lastAssistantIdx && !loading && !awaiting && messages.slice(0, idx).some((x) => x.role === 'user');
            return (
            <div key={m.id} className={`o-msg flex ${dock ? 'gap-2' : 'gap-3'} ${mine ? 'flex-row-reverse' : ''}`}>
              <div
                className={`flex shrink-0 items-center justify-center rounded-full ${dock ? 'h-7 w-7' : 'h-8 w-8'} ${
                  mine ? 'bg-primary/10' : 'bg-muted'
                }`}
              >
                {mine ? <User size={16} className="text-primary" /> : avatar(24)}
              </div>
              <div className={`flex min-w-0 flex-col gap-1 ${mine ? 'items-end' : 'items-start'} ${wide ? 'flex-1 sm:max-w-[92%]' : dock ? 'max-w-[85%]' : 'max-w-[80%]'}`}>
              <div
                className={`min-w-0 max-w-full ${wide ? 'self-stretch' : ''} rounded-2xl ${dock ? 'px-3.5' : 'px-4'} py-2.5 text-sm ${
                  mine ? 'bg-primary/10 text-foreground' : 'bg-card text-foreground'
                }`}
              >
                <div className="flex min-w-0 flex-col gap-2">
                  {!mine && m.agent && (
                    <span className="-mt-0.5 inline-flex items-center gap-1 self-start text-xs font-medium text-primary">
                      {m.agent.icon ? <span aria-hidden>{m.agent.icon}</span> : <Bot size={12} aria-hidden />}
                      <span>Respondió {m.agent.label}</span>
                    </span>
                  )}
                  {mine && m.mentions && (
                    <span className="flex flex-wrap gap-1">
                      {m.mentions.map((mm) => (
                        <MentionChip key={`${mm.kind}-${mm.id}`} item={mm} />
                      ))}
                    </span>
                  )}
                  {!mine && ((!hideThinking && m.thinking) || (showSteps && m.steps?.length)) ? (
                    <ProcessPanel thinking={hideThinking ? null : m.thinking} steps={showSteps ? m.steps : undefined} />
                  ) : null}
                  {mine
                    ? m.bloques.map((b, i) => <UserBloqueView key={i} bloque={b} />)
                    : m.bloques.map((b, i) => <BloqueView key={i} bloque={b} />)}
                  {m.stopped && (
                    <span className="text-xs text-muted-foreground">Respuesta detenida · no se guardó</span>
                  )}
                </div>
              </div>
              {!mine && messageActions && (copyable || canRegen) && (
                <MessageActions text={copyable} onRegenerate={canRegen ? () => regenerate(idx) : undefined} pinned={idx === lastAssistantIdx} />
              )}
              </div>
            </div>
            );
          })}

          {(loading || awaiting) && (
            <div className={`flex ${dock ? 'gap-2' : 'gap-3'}`}>
              <div className={`flex shrink-0 items-center justify-center rounded-full bg-muted ${dock ? 'h-7 w-7' : 'h-8 w-8'}`}>
                {avatar(24)}
              </div>
              {liveText || liveBloques.length > 0 ? (
                // 📡 Respuesta escribiéndose en vivo (se pinta una vez por frame).
                <div className={`${liveBloques.length || isWideMarkdown(liveText) ? 'min-w-0 flex-1 sm:max-w-[92%]' : dock ? 'min-w-0 max-w-[85%]' : 'min-w-0 max-w-[80%]'} rounded-2xl bg-card px-4 py-2.5 text-sm text-foreground`} aria-busy="true">
                  {showSteps && liveSteps.length > 0 && <ProcessPanel steps={liveSteps} live />}
                  {liveText && <BloqueView bloque={{ tipo: 'texto', contenido: liveText }} live />}
                  {liveBloques.map((b, i) => (
                    <div key={i} className="mt-2">
                      <BloqueView bloque={b} />
                    </div>
                  ))}
                  <span
                    aria-hidden
                    className="ml-0.5 inline-block h-3.5 w-1.5 translate-y-0.5 rounded-sm bg-primary/60 motion-safe:animate-pulse"
                  />
                </div>
              ) : (
                <div className="flex min-w-0 max-w-[85%] flex-col rounded-2xl bg-card px-4 py-3.5">
                {showSteps && liveSteps.length > 1 && <ProcessPanel steps={liveSteps} live />}
                <div
                  className="flex flex-wrap items-center gap-2"
                  aria-label={agentStatus || (awaiting ? 'Terminando la respuesta…' : think ? 'Razonando…' : 'Escribiendo…')}
                >
                  <div className="flex items-center gap-1" aria-hidden>
                    <span className="h-2 w-2 rounded-full bg-muted-foreground/70 motion-safe:animate-bounce motion-safe:[animation-delay:-0.3s]" />
                    <span className="h-2 w-2 rounded-full bg-muted-foreground/70 motion-safe:animate-bounce motion-safe:[animation-delay:-0.15s]" />
                    <span className="h-2 w-2 rounded-full bg-muted-foreground/70 motion-safe:animate-bounce" />
                  </div>
                  {/* Línea de estado del agente («Consultando leads…»): sin aria-live
                      para no anunciar cada fase; se anuncia solo la respuesta final. */}
                  {(agentStatus || awaiting) && (
                    <span key={agentStatus || 'awaiting'} className="text-xs text-muted-foreground motion-safe:animate-pulse">
                      {agentStatus || 'Terminando la respuesta…'}
                    </span>
                  )}
                  {think && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Brain size={12} className="text-primary" />
                      Razonando… (puede tardar un poco)
                    </span>
                  )}
                </div>
                </div>
              )}
            </div>
          )}

          {/* ♿ Respuesta final para lectores de pantalla (una vez, no por token). */}
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            {announce}
          </div>

          <div ref={endRef} />
        </div>
      </div>

      {error && (
        <div className="mx-auto w-full max-w-3xl px-4">
          <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
        </div>
      )}

      <div
        className={`border-t border-border px-3 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] ${dock ? '' : 'sm:px-4 sm:py-3'}`}
        {...dropHandlers}
      >
        <div className="relative mx-auto max-w-3xl">
          {menuOpen && trigger && (
            <MentionMenu
              id={menuId}
              kind={trigger.kind}
              items={menuItems}
              activeIndex={activeIdx}
              loading={menuLoading}
              onPick={pickMention}
              onHover={setActiveIdx}
            />
          )}
          {(pending.length > 0 || uploading) && (
            <div className="mb-2 flex flex-wrap gap-2" aria-label="Adjuntos de este mensaje">
              {pending.map((a) => (
                <div key={a.id} className="relative">
                  {a.preview ? (
                    <img src={a.preview} alt={a.titulo} title={a.titulo} className="h-14 w-14 rounded-lg border border-border object-cover" />
                  ) : (
                    <div className="flex h-14 max-w-[220px] items-center gap-2 rounded-lg border border-border bg-card px-3">
                      {a.clase === 'dataset' ? <Table2 size={16} className="shrink-0 text-primary" aria-hidden /> : <FileText size={16} className="shrink-0 text-primary" aria-hidden />}
                      <span className="truncate text-xs" title={a.titulo}>{a.titulo}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removePending(a.id)}
                    aria-label={`Quitar ${a.titulo}`}
                    title="Quitar adjunto"
                    className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow hover:text-foreground"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {uploading && (
                <div className="flex h-14 items-center gap-2 rounded-lg border border-dashed border-border px-3 text-xs text-muted-foreground">
                  <Loader2 size={14} className="animate-spin" aria-hidden /> Subiendo…
                </div>
              )}
            </div>
          )}
          {(activeAgentItem || skill || pickedTools.length > 0) && (
            <div className="mb-2 flex flex-wrap items-center gap-1.5" aria-label="Menciones de este mensaje">
              {activeAgentItem && (
                <MentionChip
                  item={activeAgentItem}
                  prefix="@"
                  onRemove={agentCtl ? () => setActiveAssistantId('') : undefined}
                />
              )}
              {skill && <MentionChip item={skill} prefix="/" onRemove={() => setSkill(null)} />}
              {pickedTools.map((t) => (
                <MentionChip
                  key={t.id}
                  item={t}
                  prefix="#"
                  onRemove={() => setPickedTools((l) => l.filter((x) => x.id !== t.id))}
                />
              ))}
            </div>
          )}
          {dock ? (
            // Flotante: el texto a todo lo ancho y las herramientas en una fila debajo
            // (en 380 px seis botones junto al campo lo dejaban en nada).
            <div className="flex flex-col gap-2">
              {textarea}
              <div className="flex items-center gap-1.5">
                {clipBtn}
                {modelBtn}
                {thinkBtn}
                {selfBtn}
                <span className="flex-1" />
                {voiceBtn}
                {sendBtn}
              </div>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              {/* 🧠 «Pensar» (oculto con hideThinking: el diagnóstico es entrevista, no análisis). */}
              {thinkBtn}
              {/* 🪞 «U-CogNet sobre sí mismo»: solo se ofrece a la org ucognet. */}
              {selfBtn}
              {/* 🎛️ Modelo y esfuerzo de esta conversación («Auto» por defecto). */}
              {modelBtn}
              {clipBtn}
              {textarea}
              {voiceBtn}
              {sendBtn}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatPane;
